/**
 * SessionFormScreen — schedule a session for a student (PRD §3 #3).
 *
 * v1.0-A: schedule only. v1.0-B wires the "Generate plan" CTA that
 * calls `api-generate-session-plan`. For now we capture
 * `scheduled_at` + `student_id` + an optional preset-goals
 * multi-select.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import { formatShortDate } from '../lib/effectiveslp/domain';

type SessionType = 'therapy' | 'initial_eval' | 'reeval';

interface Props {
  studentId: string;
  onSaved: () => void;
  onCancel: () => void;
}

interface Goal {
  id: string;
  area: string;
  goal_text: string;
}

export function SessionFormScreen({
  studentId,
  onSaved,
  onCancel,
}: Props): ReactElement {
  const [sessionType, setSessionType] = useState<SessionType>('therapy');
  const [scheduledAt, setScheduledAt] = useState(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
  });
  const [goalIds, setGoalIds] = useState<string[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('goals')
        .select('id, area, goal_text')
        .eq('student_id', studentId)
        .eq('status', 'active')
        .order('created_at');
      if (cancelled) return;
      setGoals((data as Goal[] | null) ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  function toggleGoal(id: string): void {
    setGoalIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const iso = new Date(scheduledAt).toISOString();
    setSaving(true);
    setError(null);
    try {
      const { data: sess, error: iErr } = await supabase
        .from('sessions')
        .insert({
          student_id: studentId,
          scheduled_at: iso,
          status: 'scheduled',
          session_type: sessionType,
        })
        .select('id')
        .single();
      if (iErr) {
        setError(iErr.message);
        return;
      }
      const sessionId = (sess as { id: string }).id;
      // Only seed carryover for therapy sessions — evals have no focus goals.
      if (sessionType === 'therapy' && goalIds.length > 0) {
        const { error: lErr } = await supabase
          .from('carryover_activities')
          .insert(
            goalIds.map((goalId) => ({
              session_id: sessionId,
              goal_id: goalId,
              activity_text: '(plan pending)',
              sort_order: 0,
            })),
          );
        if (lErr) {
          setError(lErr.message);
          return;
        }
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="screen form-screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onCancel}>
          ← Cancel
        </button>
        <h1>Schedule a session</h1>
      </header>

      <form onSubmit={(e) => void handleSubmit(e)}>
        <label>
          Session type
          <select
            value={sessionType}
            onChange={(e) => setSessionType(e.target.value as SessionType)}
          >
            <option value="therapy">Therapy</option>
            <option value="initial_eval">Initial Evaluation</option>
            <option value="reeval">Re-evaluation</option>
          </select>
        </label>

        <label>
          Scheduled at
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            required
          />
        </label>

        {sessionType === 'therapy' && (
          <fieldset className="form-fieldset">
            <legend>Preset goals</legend>
            {goals.length === 0 && (
              <p className="tagline">No active goals for this student.</p>
            )}
            {goals.map((g) => {
              const checked = goalIds.includes(g.id);
              return (
                <label key={g.id} className="caseload-row">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleGoal(g.id)}
                  />
                  <span className="caseload-row-body">
                    <span className="caseload-row-name">
                      {g.area}
                    </span>
                    <span className="caseload-row-meta">{g.goal_text}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>
        )}

        {sessionType !== 'therapy' && (
          <p className="tagline">
            {sessionType === 'initial_eval'
              ? 'The structured evaluation template will be available in the session planner after scheduling.'
              : 'Re-evaluation sessions use the same structured template as initial evaluations.'}
          </p>
        )}

        {error && <p className="error">{error}</p>}

        <div className="form-actions">
          <button type="submit" className="snap-button" disabled={saving}>
            {saving
              ? 'Saving…'
              : sessionType === 'initial_eval'
                ? 'Schedule evaluation'
                : sessionType === 'reeval'
                  ? 'Schedule re-evaluation'
                  : 'Schedule session'}
          </button>
        </div>
      </form>

      <footer className="screen-footer">
        Last planning reference: {formatShortDate(new Date().toISOString())}
      </footer>
    </main>
  );
}
